import { Chip } from '@mui/joy';
import { t } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser, useUserPermissions } from 'auth/hooks';
import { InfoCard } from 'core/components';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { TextLine, Tooltip } from 'core/elements';
import { AccessPermissions } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { formatDate, getValue } from 'core/utils';
import { filenetStatusColorMap, statusColorMap } from 'quotes/constants';
import {
  FilenetStatus,
  FilenetStatusLabel,
  QuoteEntityType,
  QuoteStatus,
  QuoteStatusLabel,
} from 'quotes/enums';
import { VendorQuoteHelper } from 'quotes/helpers';
import { IVendorQuote } from 'quotes/types';
import { Color, Size, Variant } from 'theme/enums';

import { getLinkValue, getValidValue } from './utils';
import { UpdateVendorQuoteMainInfo } from '../UpdateVendorQuoteMainInfo';

interface IProps {
  quote: IVendorQuote;
  entityType?: QuoteEntityType;
}

export const MainInfo = ({ quote, entityType = QuoteEntityType.QUOTE }: IProps) => {
  const [isEditOpen, setIsEditOpen] = useState(false);

  const { user } = useAuthUser();
  const { hasPermission } = useUserPermissions();

  // FileNet status + resolved supplier are alternate-platform data — hidden for classic users.
  const showFilenetInfo = AlternateVersionHelper.isAlternateVersionEnabled({ user });

  const { t } = useTranslation();

  const isEditable = VendorQuoteHelper.canEditImportedVendorQuote({
    quote,
    quoteEntityType: entityType,
    hasVendorQuoteEditPermission: hasPermission(AccessPermissions.ACCESS_COMPANY_VENDOR_QUOTE_EDIT),
  });

  const items = getMainInfoItems(quote, entityType, showFilenetInfo);

  return (
    <>
      <InfoCard
        title={t('titleMainInfo')}
        items={items}
        onEdit={() => setIsEditOpen(true)}
        isEditable={isEditable}
      />

      {isEditable && (
        <UpdateVendorQuoteMainInfo
          quote={quote}
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          showSupplier={showFilenetInfo}
        />
      )}
    </>
  );
};

const getMainInfoItems = (
  quote: IVendorQuote,
  entityType = QuoteEntityType.QUOTE,
  showFilenetInfo = false
) => {
  const {
    createdAtItem,
    vendorQuoteNumberItem,
    revisionNumberItem,
    vendorItem,
    statusItem,
    fileNetStatusItem,
    validItem,
    externalOrderNumberItem,
    externalPendingOrderNumberItem,
    purchaseOrderNumberItem,
    customerNumberItem,
  } = getAllMainInfoItems(quote, showFilenetInfo);

  switch (entityType) {
    case QuoteEntityType.ORDER:
      return [
        createdAtItem,
        externalOrderNumberItem,
        externalPendingOrderNumberItem,
        statusItem,
        purchaseOrderNumberItem,
        customerNumberItem,
      ];
    case QuoteEntityType.QUOTE:
    default:
      return [
        createdAtItem,
        vendorQuoteNumberItem,
        revisionNumberItem,
        vendorItem,
        statusItem,
        fileNetStatusItem,
        validItem,
      ].filter((item) => !!item);
  }
};

const getFileNetStatusValue = (quote: IVendorQuote) => {
  const { filenetStatus, filenetError } = quote ?? {};
  if (!filenetStatus) {
    return (
      <Chip color={Color.NEUTRAL} variant={Variant.SOLID} size={Size.SM}>
        {t('labelNotSentToFileNet')}
      </Chip>
    );
  }

  const chip = (
    <Chip color={filenetStatusColorMap[filenetStatus]} variant={Variant.SOLID} size={Size.SM}>
      {FilenetStatusLabel[filenetStatus]}
    </Chip>
  );

  if (FilenetStatus.FAILED === filenetStatus && filenetError) {
    return (
      <Tooltip color={Color.DANGER} title={filenetError}>
        {chip}
      </Tooltip>
    );
  }

  return chip;
};

const getAllMainInfoItems = (quote: IVendorQuote, showFilenetInfo = false) => {
  // Supplier + FileNet status only exist for imported VQs — the import is what resolves them.
  const showImportedFilenetInfo =
    showFilenetInfo && QuoteStatus.EDITABLE_BY_CUSTOMER === quote?.status;

  return {
    createdAtItem: { label: t('labelCreated'), value: formatDate(quote?.createdAt) },
    vendorQuoteNumberItem: {
      label: t('labelVendorQuoteNumberShort', { entityType: QuoteEntityType.QUOTE }),
      value: quote?.vendorQuoteNumber || DEFAULT_PLACEHOLDER,
    },
    revisionNumberItem: {
      label: t('labelQuoteRevision'),
      value: quote?.revisionNumber || DEFAULT_PLACEHOLDER,
    },
    vendorItem: showImportedFilenetInfo
      ? {
          label: t('labelVendor'),
          value: quote?.vendorLocationName || quote?.vendorName || DEFAULT_PLACEHOLDER,
          extra: quote?.vendorApNumber ? (
            <TextLine text={quote.vendorApNumber} isSubText />
          ) : undefined,
        }
      : undefined,
    fileNetStatusItem: showImportedFilenetInfo
      ? {
          label: t('labelSentToFileNet'),
          value: getFileNetStatusValue(quote),
        }
      : undefined,
    statusItem: {
      label: t('labelStatus'),
      value: (
        <Chip color={statusColorMap[quote.status]} variant={Variant.SOLID} size={Size.SM}>
          {QuoteStatusLabel[quote.status]}
        </Chip>
      ),
    },
    validItem: {
      label: t('labelValid'),
      value: getValidValue(quote?.startDate, quote?.validUntil),
    },
    externalOrderNumberItem: {
      label: t('labelExternalOrderNumber'),
      value: getLinkValue(getValue(quote, 'externalOrderNumber'), quote?.externalOrderUrl),
    },
    externalPendingOrderNumberItem: {
      label: t('labelExternalPendingOrderNumber'),
      value: getLinkValue(
        getValue(quote, 'externalPendingOrderNumber'),
        quote?.externalPendingOrderUrl
      ),
    },
    purchaseOrderNumberItem: {
      label: t('labelPurchaseOrderNumber'),
      value: getValue(quote, 'purchaseOrderNumber'),
    },
    customerNumberItem: {
      label: t('labelCustomerNumber'),
      value: getValue(quote, 'externalCustomerNumber'),
    },
  };
};
